import PropTypes from 'prop-types';
import React, { useState, useCallback, useEffect } from 'react';

import { graphQLQueries } from 'javascripts/shared/graphql/queries';
import { graphQL } from 'javascripts/shared/graphql';

import { bubEvents } from 'javascripts/shared/events/bub_events';
import { urlUtils } from 'javascripts/shared/utils/url_utils';

import { AssetImg } from 'javascripts/components/asset_img';
import { ChirpButton } from 'javascripts/components/chirp_button';
import { StarRating } from 'javascripts/components/star_rating';
import { ReviewCard } from 'javascripts/components/reviews/review_card';
import { Loader } from 'javascripts/components/loader';
import { LoadMoreButton } from 'javascripts/components/search/load_more_button';

import starImage from 'images/stars.jpg';
import styles from 'stylesheets/components/audiobook/audiobook_reviews.module';

export function AudiobookReviews(props) {
  const [allReviews, setAllReviews] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [currentAudiobook, setCurrentAudiobook] = useState({});

  const reviewLink = `/user_reviews/audiobook/${props.audiobookId}?rta=t`;

  const fetchData = useCallback(() => {
    graphQL
      .get(graphQLQueries.userReviews, { audiobookId: props.audiobookId, page: currentPage, pageSize: 5 })
      .then(({ audiobook }) => {
        setCurrentAudiobook(audiobook);
        setAllReviews([...allReviews, ...audiobook.userReviews.objects]);
        setLoading(false);
      })
      .catch(error => {
        setLoading(false);
        throw error;
      });
  }, [currentPage]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  function handleLoadMoreClick() {
    bubEvents.track('Review', 'Clicked load more reviews', {
      label: 'Load reviews',
      value: currentPage + 1,
    });

    setCurrentPage(currentPage + 1);
  }

  function renderReviewCta() {
    if (!props.hasUserPurchased || loading) {
      return null;
    }
    return (
      <ChirpButton
        class={styles.reviewCta}
        displayType='link'
        href={reviewLink}
        label={currentAudiobook.reviewedByCurrentUser ? 'See Review' : 'Add Review'}
      />
    );
  }

  function renderAverageRating() {
    if (currentAudiobook.ratingsCount === 0 || !_.isFinite(currentAudiobook.averageRating)) {
      return null;
    }

    return (
      <div className={styles.averageRatingWrapper}>
        <span className={styles.averageRatingLabel}>Average Rating:</span>
        <div className={styles.starRatingWrapper}>
          <StarRating
            rating={currentAudiobook.averageRating}
            ratingsCount={currentAudiobook.ratingsCount}
            showRating
            showRatingsCount
          />
        </div>
      </div>
    );
  }

  function renderReviews() {
    return (
      <div className={styles.reviews}>
        {allReviews.map((review, index) => (
          <div className={styles.review} key={`review-${index}`}>
            <ReviewCard review={review} onEditReviewClick={() => urlUtils.setPath(reviewLink)} />
          </div>
        ))}
      </div>
    );
  }

  function renderNoReviews() {
    if (!props.hasUserPurchased) {
      return <div className={styles.noReviewsUnpurchased}>No one has reviewed this book yet.</div>;
    }

    return (
      <div className={styles.noReviewsPurchased}>
        <AssetImg src={starImage} attrs={{ className: styles.starImage, alt: 'star' }} />
        <div className={styles.noReviewsHeader}>Be the first to review!</div>
        <a className={styles.noReviewsLink} href={reviewLink}>
          Tell us what you thought
        </a>
      </div>
    );
  }

  function renderLoadMoreButton() {
    if (loading && allReviews.length > 0) {
      return <Loader />;
    }

    if (allReviews.length >= currentAudiobook.userReviews?.totalCount) {
      return null;
    }

    return (
      <div className={styles.loadMoreContainer}>
        <LoadMoreButton loadMore={handleLoadMoreClick} fullWidth />
      </div>
    );
  }

  function renderBody() {
    if (loading && allReviews.length === 0) {
      return <Loader />;
    }
    return (
      <div>
        {renderAverageRating()}
        {currentAudiobook.userReviews?.totalCount > 0 ? renderReviews() : renderNoReviews()}
        {renderLoadMoreButton()}
      </div>
    );
  }

  function renderHeader() {
    if (!props.showHeader) {
      return null;
    }

    return (
      <div className={styles.header}>
        <h2 className={styles.headerText}>Ratings & Reviews</h2>
        {renderReviewCta()}
      </div>
    );
  }

  return (
    <div className={styles.audiobookReviews} data-testid='audiobook-reviews'>
      <a name='reviews'></a>
      {renderHeader()}
      {renderBody()}
    </div>
  );
}

AudiobookReviews.propTypes = {
  audiobookId: PropTypes.string.isRequired,
  hasUserPurchased: PropTypes.bool.isRequired,
  showHeader: PropTypes.bool,
};

AudiobookReviews.defaultProps = {
  showHeader: true,
};
